import { oauthConfig, linkedInAuthUrl, appleAuthUrl } from '../config/oauth.config';
import { getRuntimeConfig, getRuntimeConfigOrDefault } from '../utils/runtime-config';

const API_BASE_URL = getRuntimeConfigOrDefault('REACT_APP_API_URL', 'http://localhost:3001/api');

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  provider: 'google' | 'linkedin' | 'apple';
  resume?: {
    url: string;
    uploadedAt: string;
  };
  salaryRange?: {
    min: number;
    max: number;
  };
  skills?: string[];
  companyId?: string;
  companyName?: string;
}

class AuthService {
  private tokenKey = 'jobometry_auth_token';
  private stateKey = 'jobometry_oauth_state';

  async login(provider: 'google' | 'linkedin' | 'apple'): Promise<void> {
    // Check if we should use real authentication
    const useRealAuth = getRuntimeConfig('REACT_APP_USE_REAL_AUTH') === 'true';
    const isDevelopment = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;
    
    // Use mock SSO in development unless explicitly using real auth
    if (isDevelopment && !useRealAuth) {
      console.log(`🔐 Mock SSO active - simulating ${provider} OAuth flow`);
      await this.mockSSOFlow(provider);
      return;
    }
    
    // Check if we should force real auth
    const forceRealAuth = getRuntimeConfig('REACT_APP_FORCE_REAL_AUTH') === 'true';
    
    // Development mode - simulate OAuth flow (unless forced to use real auth)
    if (isDevelopment && !forceRealAuth && !this.hasRealCredentials(provider)) {
      console.warn(`${provider} OAuth not configured. Using mock authentication.`);
      console.warn('To use real OAuth, set up credentials in .env file or set REACT_APP_FORCE_REAL_AUTH=true');
      
      // Simulate OAuth flow with mock data
      setTimeout(() => {
        this.handleAuthSuccess({
          token: 'mock-jwt-token-' + Date.now(),
          user: {
            id: 'mock-' + Date.now(),
            email: `demo@${provider}.com`,
            name: `Demo User (${provider})`,
            avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${provider}`,
            provider: provider
          }
        });
      }, 1000);
      return;
    }
    
    // Production mode - real OAuth flow
    const state = this.generateState();
    sessionStorage.setItem(this.stateKey, state);
    
    switch (provider) {
      case 'google':
        // For Google, we need to use the Google Sign-In SDK
        // The actual redirect will be handled by the SDK
        window.location.href = this.getGoogleAuthUrl();
        break;
        
      case 'linkedin':
        window.location.href = linkedInAuthUrl();
        break;
        
      case 'apple':
        window.location.href = appleAuthUrl();
        break;
        
      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }
  }

  private hasRealCredentials(provider: string): boolean {
    switch (provider) {
      case 'google':
        return !!process.env.REACT_APP_GOOGLE_CLIENT_ID && 
               process.env.REACT_APP_GOOGLE_CLIENT_ID !== '1234567890-abcdefghijklmnopqrstuvwxyz123456.apps.googleusercontent.com';
      case 'linkedin':
        return !!process.env.REACT_APP_LINKEDIN_CLIENT_ID && 
               process.env.REACT_APP_LINKEDIN_CLIENT_ID !== 'YOUR_LINKEDIN_CLIENT_ID';
      case 'apple':
        return !!process.env.REACT_APP_APPLE_CLIENT_ID && 
               process.env.REACT_APP_APPLE_CLIENT_ID !== 'YOUR_APPLE_CLIENT_ID';
      default:
        return false;
    }
  }

  private getGoogleAuthUrl(): string {
    const params = new URLSearchParams({
      client_id: oauthConfig.google.clientId,
      redirect_uri: oauthConfig.google.redirectUri,
      response_type: 'code',
      scope: oauthConfig.google.scope,
      access_type: 'offline',
      state: sessionStorage.getItem(this.stateKey) || ''
    });
    
    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  // Handle Google OAuth response
  async handleGoogleLogin(credential: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
      });

      if (!response.ok) {
        throw new Error('Google authentication failed');
      }

      const data = await response.json();
      this.handleAuthSuccess(data);
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }

  // Handle OAuth callback for LinkedIn and Apple
  async handleOAuthCallback(provider: string, code: string, state: string): Promise<void> {
    // Verify state for CSRF protection
    const storedState = sessionStorage.getItem(this.stateKey);
    if (state !== storedState) {
      throw new Error('Invalid state parameter');
    }
    sessionStorage.removeItem(this.stateKey);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/${provider}/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, state }),
      });

      if (!response.ok) {
        throw new Error(`${provider} authentication failed`);
      }

      const data = await response.json();
      this.handleAuthSuccess(data);
    } catch (error) {
      console.error(`${provider} callback error:`, error);
      throw error;
    }
  }

  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  private async mockSSOFlow(provider: 'google' | 'linkedin' | 'apple'): Promise<void> {
    try {
      // Show loading state
      console.log(`🔄 Starting mock ${provider} SSO flow...`);
      
      // Simulate network delay for realistic feel
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Generate deterministic but unique user data
      const timestamp = Date.now();
      const userId = `${provider}-dev-${timestamp}`;
      
      // Provider-specific mock user data
      const mockUsers = {
        google: {
          id: userId,
          email: 'dev.user@gmail.com',
          name: 'Dev User (Google)',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=google-dev',
          provider: 'google' as const
        },
        linkedin: {
          id: userId,
          email: 'dev.user@linkedin.com',
          name: 'Dev User (LinkedIn)',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=linkedin-dev',
          provider: 'linkedin' as const
        },
        apple: {
          id: userId,
          email: 'dev.user@icloud.com',
          name: 'Dev User (Apple)',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=apple-dev',
          provider: 'apple' as const
        }
      };
      
      const mockUser = mockUsers[provider];
      
      // Generate mock JWT token with proper format
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
      const payload = btoa(JSON.stringify({ 
        userId: userId,
        id: userId,
        sub: userId,
        email: mockUser.email,
        name: mockUser.name,
        provider: provider,
        iat: Math.floor(timestamp / 1000),
        exp: Math.floor(timestamp / 1000) + 86400 // 24 hours
      }));
      const signature = 'mock-signature';
      const mockToken = `${header}.${payload}.${signature}`;
      
      // Log successful mock authentication
      console.log(`✅ Mock ${provider} SSO successful!`);
      console.log(`👤 User: ${mockUser.name} (${mockUser.email})`);
      console.log(`🎫 Token: ${mockToken.substring(0, 20)}...`);
      
      // Store token first
      localStorage.setItem(this.tokenKey, mockToken);
      
      // Create session (using sessionService if available)
      if (typeof window !== 'undefined' && (window as any).sessionService) {
        (window as any).sessionService.createSession(mockToken, {
          userId: userId,
          email: mockUser.email,
          name: mockUser.name
        });
      }
      
      // Then trigger the auth success event
      window.dispatchEvent(new CustomEvent('auth:login', { detail: mockUser }));
      
      // Don't redirect - let the app handle navigation
      console.log('🔄 Authentication complete - updating UI...');
      
    } catch (error) {
      console.error('❌ Mock SSO error:', error);
      throw error;
    }
  }

  private handleAuthSuccess(response: { token: string; user: UserProfile }) {
    localStorage.setItem(this.tokenKey, response.token);
    // Trigger a custom event to notify the app of successful login
    window.dispatchEvent(new CustomEvent('auth:login', { detail: response.user }));
    
    // Redirect to home page or previous page
    const redirectUrl = sessionStorage.getItem('auth_redirect') || '/';
    sessionStorage.removeItem('auth_redirect');
    window.location.href = redirectUrl;
  }

  async logout(): Promise<void> {
    try {
      const token = this.getToken();
      if (token) {
        // Call logout endpoint
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(this.tokenKey);
    }
  }

  async getMe(): Promise<UserProfile | null> {
    const token = this.getToken();
    if (!token) return null;

    // In development mode without a real backend, return mock user data
    if (process.env.NODE_ENV === 'development' && (!process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL.includes('localhost'))) {
      try {
        // Decode the mock token to get user info
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          return {
            id: payload.userId || payload.id,
            email: payload.email,
            name: payload.name,
            provider: payload.provider,
            avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${payload.provider}-dev`
          };
        }
      } catch (e) {
        console.log('Failed to decode mock token, returning default user');
      }
      
      // Return a default mock user if token parsing fails
      return {
        id: 'mock-user-123',
        email: 'demo@jobometry.com',
        name: 'Demo User',
        provider: 'google'
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          localStorage.removeItem(this.tokenKey);
          return null;
        }
        throw new Error('Failed to fetch user');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get user error:', error);
      // In development, don't clear the token on network errors
      if (process.env.NODE_ENV !== 'development') {
        localStorage.removeItem(this.tokenKey);
      }
      return null;
    }
  }

  async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
    const token = this.getToken();
    if (!token) throw new Error('Not authenticated');

    // For demo, return updated mock data
    const currentUser = await this.getMe();
    if (!currentUser) throw new Error('User not found');
    
    return { ...currentUser, ...updates };

    // Real implementation:
    // const response = await fetch(`${API_BASE_URL}/profile`, {
    //   method: 'PUT',
    //   headers: {
    //     'Authorization': `Bearer ${token}`,
    //     'Content-Type': 'application/json'
    //   },
    //   body: JSON.stringify(updates)
    // });
    // if (!response.ok) throw new Error('Failed to update profile');
    // return await response.json();
  }

  async uploadResume(file: File): Promise<{ url: string; uploadedAt: string }> {
    const token = this.getToken();
    if (!token) throw new Error('Not authenticated');

    const formData = new FormData();
    formData.append('resume', file);

    // For demo, return mock data
    return {
      url: '/mock-resume.pdf',
      uploadedAt: new Date().toISOString()
    };

    // Real implementation:
    // const response = await fetch(`${API_BASE_URL}/profile/resume`, {
    //   method: 'POST',
    //   headers: {
    //     'Authorization': `Bearer ${token}`
    //   },
    //   body: formData
    // });
    // if (!response.ok) throw new Error('Failed to upload resume');
    // return await response.json();
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

const authService = new AuthService();
export default authService;